import type { CalendarDefinition, StructuredCalendarDefinition } from './calendar-definition.js';
import { mod } from './leap.js';
import {
  isCycleBag,
  normalizeCycles,
  type Calendar,
  type CycleBag,
  type CycleDefinition,
  type CycleInstance,
  type LabelSpec,
  type OpenCycleInstance,
} from './types.js';

/**
 * `cycles.ts` — the multi-cycle runtime engine (A2, #231; `06-timeline.md`
 * §4).
 *
 * A calendar declares a **bag of parallel named cycles** (§4): each cycle
 * is, in essence, a pure function `day -> position` (and optionally
 * `day -> label`). This module evaluates that bag over the canonical-day
 * axis:
 *
 *   - **atomic** cycles run from a phase offset over a fixed `period_days`
 *     (`(day - phase) mod period`, mathematical modulo);
 *   - **nested** cycles compose from a `based_on` child cycle, sized by a
 *     `NestedPattern` (regular: constant children-per-instance; irregular:
 *     an explicit per-instance length list). Resolution walks the day axis
 *     cyclically and reports `(instance, position-within-instance)`.
 *
 * The engine reads the calendar's cycles through {@link calendarBag}, which
 * folds the legacy `{ year, week? }` runtime arm into the canonical bag via
 * `normalizeCycles`. So the same engine drives both bag-form definitions
 * (A1, #230) and the legacy bundled calendars (gregorian, harptos) off one
 * code path — the "drive display off the bag" requirement of A2.
 *
 * Scope boundaries:
 *
 *   - **Open cycles** (`§4.1`, ASOIAF seasons / WoT Ages) land here (A3,
 *     #232): an `open` cycle's instances carry an explicit `start_day` and
 *     an optionally-unset `end_day`. {@link cyclePosition} resolves a day
 *     inside a closed instance, and a day inside a still-open instance as
 *     the "current open instance" with `unknownLength: true`. A GM
 *     `close_at(day)` fills `end_day` via {@link closeOpenCycleInstance} (a
 *     `pack.define_calendar` definition update — LWW — never a fact).
 *   - **Per-cycle intercalary phase-advance** (`§4.2`) lands here (A3):
 *     each cycle declares `intercalary.advances_cycle`; a non-advancing
 *     atomic cycle (Shire week) does NOT advance across an intercalary day,
 *     an advancing one (Harptos tenday) does. The policy is per-cycle, so
 *     parallel cycles on the SAME intercalary day resolve independently.
 *   - **`product` / `formula` labels** (`§4.3`, sexagenary / Tzolkin) land
 *     here (A4, #233). {@link cycleLabel} now resolves all three LabelSpec
 *     kinds: `list` (`labels[pos % n]`), `product` (join the factor cycles'
 *     labels; period = LCM of the factor periods — sexagenary "Wood Rat",
 *     Tibetan Rabjung), and `formula` (expand a template string's
 *     `{cycle}` / `{cycle#}` placeholders against the named cycles'
 *     labels / positions). Tzolkin (a 260-day product) and Haab (a 365-day
 *     nested cycle) co-resolve as parallel cycles on the same canonical day.
 *     {@link cyclePeriodDays} exposes the (possibly compound) period.
 *
 * The engine is **pure and deterministic** — no `Date`, no `Math.random`,
 * no functions in data. All day arithmetic that needs a sign-correct
 * modulo routes through `mod` (the same helper the leap rule and the
 * weekday projection already use).
 *
 * **Leap rules.** The bag the engine reads is *static* — it does not encode
 * the calendar's `leap_rule`. The conversion engine (`convert.ts`) keeps
 * the leap-aware year/month walk because a leap day is an irregularity in a
 * *specific year's* month lengths, not a property of the cycle shape. So
 * {@link cyclePosition} on a `month`/`year` cycle is the *leap-unaware*
 * structural position (every instance the same as the declared
 * `per_instance` length); the era/leap-aware `DisplayDate` decomposition
 * stays in `convert.ts`. Atomic cycles (weekday, tenday, stem-branch) have
 * no leap interaction and are fully resolved here.
 */

// ---------- public position type ----------

/**
 * Where a canonical day sits inside a named cycle.
 *
 * - `cycle` — the resolved cycle's name.
 * - `instance` — for a **nested** cycle, the signed index of the instance
 *   the day falls in (instance 0 begins at the cycle's phase day 0; a
 *   negative day can land in instance -1, -2, …). For an **atomic** cycle
 *   `instance` is always 0 (an atomic cycle has a single repeating
 *   instance).
 * - `position` — the 0-indexed offset of the day within that instance: for
 *   an atomic cycle, `(day - phase) mod period`; for a nested cycle, the
 *   day's distance from the start of its instance.
 * - `instanceName` — the optional declared name of the instance (a month
 *   name like `"frost"`, a season name like `"winter"`), when the pattern
 *   or open instance carries one.
 * - `unknownLength` — set on an **open** cycle (§4.1) when the resolved
 *   instance is still open (`end_day` unset): the day is inside the
 *   "current open instance" but the instance's length is not yet known
 *   ("winter is coming"). Absent for closed / nested / atomic positions.
 */
export interface CyclePosition {
  readonly cycle: string;
  readonly instance: number;
  readonly position: number;
  readonly instanceName?: string;
  readonly unknownLength?: boolean;
}

// ---------- bag access ----------

/**
 * The calendar's cycle bag in canonical form. A runtime `Calendar` carries
 * either the legacy `{ year, week? }` arm or (once A2/A3 widen the runtime
 * schema) a bag; `normalizeCycles` folds the legacy arm into the bag form,
 * and returns a bag unchanged. This is the single read-seam the engine
 * goes through (mirrors `months.ts`' role for the conversion engine).
 */
export function calendarBag(calendar: Calendar): CycleBag {
  return normalizeCycles(calendar.cycles);
}

function findCycle(bag: CycleBag, name: string): CycleDefinition | undefined {
  return bag.find((c) => c.name === name);
}

/**
 * The phase offset of an atomic cycle: the index into its period of the
 * value at canonical day 0. The legacy weekday cycle carried an explicit
 * `phase`; `normalizeCycles` does not currently surface it on the bag
 * (atomic cycles default to phase 0), so the engine reads the calendar's
 * legacy week phase when resolving the synthesized `week` cycle. For a
 * hand-authored bag every atomic cycle starts at phase 0 (day 0 == position
 * 0) — the §4.1 "runs forever from day 0" default.
 */
function atomicPhase(calendar: Calendar, cycleName: string): number {
  if (!isCycleBag(calendar.cycles) && cycleName === 'week') {
    return calendar.cycles.week?.phase ?? 0;
  }
  return 0;
}

// ---------- position resolution ----------

/**
 * Resolve a canonical `day` to its position inside the named cycle, or
 * `null` when the cycle is not declared / not yet interpretable.
 *
 * Returns `null` for:
 *   - an unknown `cycleName`;
 *   - an `open` cycle (A3, #232 — not yet interpreted here);
 *   - a `nested` cycle whose `based_on` chain does not bottom out at the
 *     implicit `day` cycle (a malformed bag).
 *
 * Pure and deterministic; negative days use mathematical modulo.
 */
export function cyclePosition(
  calendar: Calendar,
  cycleName: string,
  day: number,
): CyclePosition | null {
  const bag = calendarBag(calendar);
  const cycle = findCycle(bag, cycleName);
  if (cycle === undefined) return null;
  return resolvePosition(calendar, bag, cycle, day);
}

function resolvePosition(
  calendar: Calendar,
  bag: CycleBag,
  cycle: CycleDefinition,
  day: number,
): CyclePosition | null {
  const structure = cycle.structure;
  switch (structure.kind) {
    case 'atomic': {
      const phase = atomicPhase(calendar, cycle.name);
      // §4.2: an atomic cycle that does NOT advance over intercalary days
      // counts only the real (non-intercalary) days; the festival days are
      // transparent to its phase. An advancing cycle (the default) counts
      // every day. `effectiveDay` is the day index the cycle has actually
      // advanced through — `day` minus the intercalary days it skips.
      const effectiveDay = advancesOverIntercalary(cycle)
        ? day
        : day - intercalaryDaysBefore(bag, day);
      const position = mod(effectiveDay - phase, structure.period_days);
      return { cycle: cycle.name, instance: 0, position };
    }
    case 'nested':
      return resolveNested(calendar, bag, cycle, day);
    case 'open':
      return resolveOpen(cycle.name, structure.instances, day);
  }
}

/**
 * Whether an atomic cycle advances its phase across an intercalary day
 * (§4.2). The policy is per-cycle: a non-advancing cycle (Shire 7-day week)
 * treats festival days as transparent; an advancing cycle (Harptos tenday)
 * counts them. The field is optional — an undeclared cycle **defaults to
 * advancing** (counts every day), which keeps the pre-A3 behavior
 * byte-identical for every calendar without an explicit non-advancing
 * policy. (Note the legacy `{ year, week }` normalizer DOES declare its
 * synthesized `week` cycle non-advancing — §4.1 "legacy weeks did not
 * advance over festival days".)
 */
function advancesOverIntercalary(cycle: CycleDefinition): boolean {
  return cycle.intercalary?.advances_cycle !== false;
}

/**
 * Resolve a nested cycle. A nested cycle is built from instances of its
 * `based_on` child cycle; here the child is the implicit `day` cycle (a
 * unit period of 1) once the chain bottoms out, so an instance's `length`
 * is a count of canonical days.
 *
 * Two flows:
 *
 *   - The child is the implicit `day` cycle (`based_on === 'day'`): the
 *     instance lengths come straight from the pattern (regular: constant;
 *     irregular: the per-instance list). We walk the day axis cyclically.
 *   - The child is another *nested* cycle (year built from months): we
 *     compose by summing the child cycle's instance lengths into this
 *     cycle's instance lengths, then walk. Because the child is itself
 *     leap-unaware at the bag layer, this stays a pure structural walk.
 */
function resolveNested(
  calendar: Calendar,
  bag: CycleBag,
  cycle: CycleDefinition,
  day: number,
): CyclePosition | null {
  if (cycle.structure.kind !== 'nested') return null;
  const lengths = instanceDayLengths(bag, cycle);
  if (lengths === null || lengths.entries.length === 0) return null;

  const periodDays = lengths.entries.reduce((sum, e) => sum + e.length, 0);
  if (periodDays <= 0) return null;

  // `period` is one full pass through the instance sequence. The day's
  // 0-indexed offset within the current pass; the pass index gives the
  // instance offset across passes.
  const passLength = lengths.entries.length;
  const within = mod(day, periodDays);
  const passIndex = Math.floor((day - within) / periodDays); // signed pass count

  let remaining = within;
  for (const [i, entry] of lengths.entries.entries()) {
    if (remaining < entry.length) {
      const instance = passIndex * passLength + i;
      const result: CyclePosition = { cycle: cycle.name, instance, position: remaining };
      if (entry.name !== undefined) {
        return { ...result, instanceName: entry.name };
      }
      return result;
    }
    remaining -= entry.length;
  }
  // Unreachable: `within < periodDays` guarantees a hit above.
  return null;
}

interface InstanceLengths {
  /** One entry per instance in a single pass through the cycle. */
  readonly entries: readonly {
    readonly length: number;
    readonly name?: string;
    /** §4.2: this instance is an intercalary (festival/leap) day-block. */
    readonly intercalary?: boolean;
  }[];
}

/**
 * The per-instance day lengths of a nested cycle, in a single pass through
 * the cycle. Resolves the `based_on` chain down to the implicit `day`
 * cycle:
 *
 *   - `based_on === 'day'` — the instances are the pattern's instances,
 *     each `length` already a day count.
 *   - `based_on` names another *nested* cycle — each of THIS cycle's
 *     instances spans `children_per_instance` (regular) or an explicit
 *     count (irregular) of the child cycle's instances; the instance's day
 *     length is the sum of those child instances' day lengths. The child's
 *     own per-instance day lengths recurse through this function.
 *
 * Returns `null` for a malformed chain (a `based_on` that names a missing
 * cycle, a non-nested child that is not `day`, or a cycle nesting on
 * itself).
 */
function instanceDayLengths(
  bag: CycleBag,
  cycle: CycleDefinition,
  seen: ReadonlySet<string> = new Set(),
): InstanceLengths | null {
  if (cycle.structure.kind !== 'nested') return null;
  if (seen.has(cycle.name)) return null; // cyclic `based_on`
  const nextSeen = new Set(seen).add(cycle.name);

  const { based_on, pattern } = cycle.structure;

  // Base case: instances are measured directly in days.
  if (based_on === 'day') {
    return { entries: patternEntries(pattern) };
  }

  // Recursive case: the child is another cycle. It must be a nested cycle
  // whose own instances we can measure in days.
  const child = findCycle(bag, based_on);
  if (child === undefined) return null;
  const childLengths = instanceDayLengths(bag, child, nextSeen);
  if (childLengths === null || childLengths.entries.length === 0) return null;

  // Cumulative day length of the child cycle by child-instance index (one
  // full pass; the child repeats its sequence as the parent consumes more
  // children than the child has instances in one pass).
  const childCount = childLengths.entries.length;
  const childDayAt = (childIndex: number): number => {
    const e = childLengths.entries[mod(childIndex, childCount)]!;
    return e.length;
  };

  const entries: { length: number; name?: string }[] = [];
  let childCursor = 0;
  for (const inst of patternEntries(pattern)) {
    // `inst.length` here is a count of CHILD instances, not days: sum the
    // day lengths of the next `inst.length` child instances.
    let days = 0;
    let consumed = 0;
    while (consumed < inst.length) {
      days += childDayAt(childCursor);
      childCursor++;
      consumed++;
    }
    const entry: { length: number; name?: string } = { length: days };
    if (inst.name !== undefined) entry.name = inst.name;
    entries.push(entry);
  }
  return { entries };
}

/**
 * The instances of a `NestedPattern` as `{ length, name? }` entries. For a
 * `regular` pattern there is exactly ONE entry whose `length` is the
 * constant children-per-instance count — every instance is identical, so a
 * single representative entry suffices for the cyclic walk. For an
 * `irregular` pattern the entries are the explicit per-instance list.
 */
function patternEntries(
  pattern: Extract<CycleDefinition['structure'], { kind: 'nested' }>['pattern'],
): { length: number; name?: string; intercalary?: boolean }[] {
  if (pattern.kind === 'regular') {
    return [{ length: pattern.children_per_instance }];
  }
  return pattern.per_instance.map((inst: CycleInstance) => {
    const entry: { length: number; name?: string; intercalary?: boolean } = { length: inst.length };
    if (inst.name !== undefined) entry.name = inst.name;
    if (inst.intercalary !== undefined) entry.intercalary = inst.intercalary;
    return entry;
  });
}

// ---------- open cycles (§4.1) ----------

/**
 * Resolve a canonical `day` to its position inside an **open** cycle
 * (§4.1, ASOIAF seasons / WoT Ages). Each instance carries an explicit
 * `start_day`; a closed instance also carries `end_day` (exclusive). The
 * day belongs to the LAST instance whose `start_day <= day` that still
 * contains it — a closed instance contains `day` iff `day < end_day`; a
 * still-open instance (no `end_day`) contains every day from `start_day`
 * onward, UNLESS a later instance's `start_day` has been reached (a later
 * sibling implicitly bounds an earlier open instance).
 *
 *   - Position is `day - start_day` (0 on the instance's first day).
 *   - `instance` is the instance's index in the declared array.
 *   - A still-open instance reports `unknownLength: true` ("winter is
 *     coming" — the segment's length is determined retrospectively).
 *   - A day before every instance's `start_day` (or in a gap a closed
 *     instance already ended without a successor covering it) resolves to
 *     `null` — it is not in any declared segment.
 *
 * Instances are interpreted in declared order; the engine does not assume
 * they are sorted, scanning for the covering segment directly.
 */
function resolveOpen(
  cycleName: string,
  instances: readonly OpenCycleInstance[],
  day: number,
): CyclePosition | null {
  // The covering instance is the one whose half-open span [start, end)
  // contains `day`. For a still-open instance the effective end is the
  // smallest later `start_day` strictly greater than this instance's
  // start (a later sibling bounds it), or +∞ when none exists.
  let best: { index: number; inst: OpenCycleInstance; end: number } | null = null;
  for (const [index, inst] of instances.entries()) {
    if (day < inst.start_day) continue;
    const end = inst.end_day ?? nextStartAfter(instances, inst.start_day);
    if (day >= end) continue;
    // Pick the instance with the latest start_day that still covers `day`
    // (handles overlapping/nested declarations deterministically).
    if (best === null || inst.start_day > best.inst.start_day) {
      best = { index, inst, end };
    }
  }
  if (best === null) return null;

  const result: CyclePosition = {
    cycle: cycleName,
    instance: best.index,
    position: day - best.inst.start_day,
  };
  const withName = best.inst.name !== undefined ? { ...result, instanceName: best.inst.name } : result;
  // Still-open instance: length unknown.
  if (best.inst.end_day === undefined && best.end === Number.POSITIVE_INFINITY) {
    return { ...withName, unknownLength: true };
  }
  return withName;
}

/**
 * The smallest instance `start_day` strictly greater than `after`, or
 * `+∞` when none exists. Used to bound a still-open instance by its next
 * declared sibling.
 */
function nextStartAfter(instances: readonly OpenCycleInstance[], after: number): number {
  let next = Number.POSITIVE_INFINITY;
  for (const inst of instances) {
    if (inst.start_day > after && inst.start_day < next) next = inst.start_day;
  }
  return next;
}

/**
 * Fill the `end_day` of an open-cycle instance on a calendar **definition**
 * (the GM `close_at(day)` action, §4.1). Returns a NEW definition with the
 * named open cycle's instance at `instanceIndex` carrying `end_day =
 * closeDay`; the input is never mutated. This is the value a
 * `pack.define_calendar` op carries — closing an open instance is an
 * LWW **definition update**, NOT a fact (the op log's `__pack` meta-op
 * chain is last-writer-wins per calendar id).
 *
 * Returns `null` (a no-op the caller can ignore) when:
 *   - `cycleName` is unknown or is not an `open` cycle;
 *   - `instanceIndex` is out of range;
 *   - `closeDay < start_day` (a non-sensical, zero-or-negative-length span).
 *
 * Re-closing an already-closed instance overwrites its `end_day` (an LWW
 * correction) — that is intentional, the definition is the source of truth.
 */
export function closeOpenCycleInstance(
  definition: CalendarDefinition,
  cycleName: string,
  instanceIndex: number,
  closeDay: number,
): StructuredCalendarDefinition | null {
  // A plugin-mode calendar has no structured cycles to close (#242).
  if (definition.mode === 'plugin') return null;
  const cycles = definition.cycles;
  // Open cycles only exist in bag-form definitions (the legacy `{ year,
  // week }` arm has no open cycle), so a non-bag `cycles` has nothing to
  // close.
  if (!isCycleBag(cycles)) return null;

  const cycleAt = cycles.findIndex(
    (c) => c.name === cycleName && c.structure.kind === 'open',
  );
  if (cycleAt < 0) return null;

  const cycle = cycles[cycleAt]!;
  if (cycle.structure.kind !== 'open') return null;
  const instances = cycle.structure.instances;
  const target = instances[instanceIndex];
  if (target === undefined) return null;
  if (closeDay < target.start_day) return null;

  const nextInstances = instances.map((inst, i) =>
    i === instanceIndex ? { ...inst, end_day: closeDay } : inst,
  );
  const nextCycle: CycleDefinition = {
    ...cycle,
    structure: { ...cycle.structure, instances: nextInstances },
  };
  const nextCycles = cycles.map((c, i) => (i === cycleAt ? nextCycle : c));
  return { ...definition, cycles: nextCycles };
}

// ---------- intercalary days (§4.2) ----------

/**
 * Count the intercalary days in `[0, day)` (for `day >= 0`) — i.e. the
 * festival days a non-advancing atomic cycle skips between canonical day 0
 * and `day`. For `day < 0` the count is the NEGATION of the intercalary
 * days in `[day, 0)`, so the phase offset stays sign-correct on the
 * negative axis.
 *
 * Intercalary days are a property of the calendar's **day-structure**: the
 * `month`-style nested cycle's instances marked `intercalary: true` (Shire
 * Midyear's Day, Harptos festivals). The count walks that cycle's
 * per-instance day lengths cyclically — the same structural walk
 * {@link resolveNested} performs — summing the lengths of intercalary
 * instances passed before `day`.
 *
 * When the calendar declares no intercalary day-structure (the common
 * case), every cycle behaves as if advancing and this returns 0.
 */
function intercalaryDaysBefore(bag: CycleBag, day: number): number {
  if (day === 0) return 0;
  const month = dayStructureCycle(bag);
  if (month === null) return 0;
  const lengths = instanceDayLengths(bag, month);
  if (lengths === null || lengths.entries.length === 0) return 0;

  const periodDays = lengths.entries.reduce((sum, e) => sum + e.length, 0);
  if (periodDays <= 0) return 0;

  if (day > 0) return countIntercalaryDays(lengths, periodDays, 0, day);
  // Negative: count festival days in [day, 0) and negate (so subtracting
  // the count shifts the phase the right way on the negative axis).
  return -countIntercalaryDays(lengths, periodDays, day, 0);
}

/**
 * The nested cycle that defines the calendar's day-structure (the
 * `month`-style cycle whose instances carry the intercalary flags). It is
 * the nested cycle `based_on "day"` carrying an irregular per-instance
 * list — the same cycle {@link legacyCyclesFromBag} reads for month
 * decomposition. Returns `null` when the bag declares none (no intercalary
 * structure to honor).
 */
function dayStructureCycle(bag: CycleBag): CycleDefinition | null {
  const month = bag.find(
    (c) =>
      c.structure.kind === 'nested' &&
      c.structure.based_on === 'day' &&
      c.structure.pattern.kind === 'irregular',
  );
  return month ?? null;
}

/**
 * Count the canonical days within an intercalary instance in the half-open
 * range `[from, to)`. Walks the day axis cyclically through the day-
 * structure cycle's instances (mathematical modulo), summing the days that
 * fall inside an instance whose pattern entry is intercalary.
 */
function countIntercalaryDays(
  lengths: InstanceLengths,
  periodDays: number,
  from: number,
  to: number,
): number {
  let count = 0;
  for (let d = from; d < to; d++) {
    if (isIntercalaryDay(lengths, periodDays, d)) count++;
  }
  return count;
}

/** Whether canonical day `d` falls inside an intercalary instance. */
function isIntercalaryDay(lengths: InstanceLengths, periodDays: number, d: number): boolean {
  let remaining = mod(d, periodDays);
  for (const entry of lengths.entries) {
    if (remaining < entry.length) return entry.intercalary === true;
    remaining -= entry.length;
  }
  return false;
}

// ---------- compound periods (§4.3) ----------

/** Greatest common divisor (Euclid), over non-negative integers. */
function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}

/** Least common multiple. `lcm(0, n)` is 0 (a degenerate empty factor). */
function lcm(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return Math.abs(a / gcd(a, b) * b);
}

/**
 * The structural period (in canonical days) of a named cycle, or `null`
 * when the cycle is unknown / not measurable:
 *
 *   - **atomic** — its declared `period_days`.
 *   - **nested** — the sum of one full pass of its instance day-lengths
 *     (year = sum of its months; month = sum of its days).
 *   - a cycle carrying a **`product`** LabelSpec — the LCM of its factor
 *     cycles' periods (§4.3: the sexagenary 10×12 re-aligns every 60, the
 *     Tibetan Rabjung likewise). This is the period over which the joined
 *     label sequence repeats, independent of the cycle's own `period_days`.
 *   - **open** cycles have no fixed period — `null`.
 *
 * Pure and deterministic. Used by the recurrence engine (B-track) to know
 * a labeled anchor's repeat interval, and asserted by the A4 golden tests.
 */
export function cyclePeriodDays(calendar: Calendar, cycleName: string): number | null {
  const bag = calendarBag(calendar);
  const cycle = findCycle(bag, cycleName);
  if (cycle === undefined) return null;
  return cyclePeriodDaysFor(bag, cycle, new Set());
}

function cyclePeriodDaysFor(
  bag: CycleBag,
  cycle: CycleDefinition,
  seen: ReadonlySet<string>,
): number | null {
  // A `product` LabelSpec defines its own compound period (LCM of factors),
  // which takes precedence over the cycle's structural `period_days`. This
  // is what makes a `{ atomic period_days: 60, labels: product }` cycle and
  // a bare `product` cycle agree on 60.
  if (cycle.labels?.kind === 'product') {
    return productPeriodDays(bag, cycle.labels, seen.has(cycle.name) ? seen : addSeen(seen, cycle.name));
  }
  const structure = cycle.structure;
  switch (structure.kind) {
    case 'atomic':
      return structure.period_days;
    case 'nested': {
      const lengths = instanceDayLengths(bag, cycle);
      if (lengths === null || lengths.entries.length === 0) return null;
      const total = lengths.entries.reduce((sum, e) => sum + e.length, 0);
      return total > 0 ? total : null;
    }
    case 'open':
      return null;
  }
}

function addSeen(seen: ReadonlySet<string>, name: string): ReadonlySet<string> {
  return new Set(seen).add(name);
}

/**
 * The LCM of a `product` LabelSpec's factor cycles' periods (§4.3). Returns
 * `null` when any factor cycle is unknown or has no measurable period (the
 * spec is malformed). Guards against a product factor referencing the
 * product cycle itself (a cyclic definition) via the `seen` set.
 */
function productPeriodDays(
  bag: CycleBag,
  spec: Extract<LabelSpec, { kind: 'product' }>,
  seen: ReadonlySet<string>,
): number | null {
  let period = 1;
  for (const factor of spec.factors) {
    if (seen.has(factor.cycle)) return null; // cyclic product definition
    const fc = findCycle(bag, factor.cycle);
    if (fc === undefined) return null;
    const fp = cyclePeriodDaysFor(bag, fc, addSeen(seen, factor.cycle));
    if (fp === null || fp <= 0) return null;
    period = lcm(period, fp);
    if (period === 0) return null;
  }
  return period;
}

// ---------- label resolution ----------

/**
 * Resolve a canonical `day` to the named cycle's label, or `null` when the
 * cycle declares no label or cannot be positioned. All three §4.3 LabelSpec
 * kinds resolve:
 *
 *   - **`list`** — `labels[index % labels.length]`, where `index` is the
 *     atomic phase position (weekday names) or the nested instance index
 *     (month names). Mathematical modulo, so negative days resolve.
 *   - **`product`** (§4.3, sexagenary / Rabjung) — join the factor cycles'
 *     own labels at `day` with a single space ("Wood Rat"). The product's
 *     period is the LCM of the factor periods ({@link cyclePeriodDays}); the
 *     joined sequence therefore repeats every LCM days. A factor that has no
 *     label of its own contributes its numeric position.
 *   - **`formula`** (§4.3) — expand a template string's placeholders against
 *     other named cycles: `{cycle}` → that cycle's label (or its position
 *     when it has no label); `{cycle#}` → its numeric position; `{cycle#1}`
 *     (any digit suffix `k`) → position offset by `k` (1-indexed display).
 *     An unknown placeholder cycle expands to the empty string.
 *
 * Pure and deterministic; never throws on a malformed factor/placeholder —
 * it degrades (a missing factor → its position; a missing placeholder → "").
 */
export function cycleLabel(calendar: Calendar, cycleName: string, day: number): string | null {
  const bag = calendarBag(calendar);
  const cycle = findCycle(bag, cycleName);
  if (cycle === undefined) return null;
  const labels = cycle.labels;
  if (labels === undefined) return null;
  return resolveLabel(calendar, bag, cycle, labels, day, new Set());
}

/**
 * Resolve a cycle's label for one of the three LabelSpec kinds. `seen`
 * tracks the cycle names already entered so a self-referential `product`
 * factor or `formula` placeholder cannot recurse forever (it degrades to a
 * position / empty string instead).
 */
function resolveLabel(
  calendar: Calendar,
  bag: CycleBag,
  cycle: CycleDefinition,
  labels: LabelSpec,
  day: number,
  seen: ReadonlySet<string>,
): string | null {
  switch (labels.kind) {
    case 'list':
      return resolveListLabel(calendar, bag, cycle, labels, day);
    case 'product':
      return resolveProductLabel(calendar, bag, labels, day, addSeen(seen, cycle.name));
    case 'formula':
      return resolveFormulaLabel(calendar, bag, labels, day, addSeen(seen, cycle.name));
  }
}

/**
 * `list` LabelSpec: `labels[index % n]`. For an atomic cycle the index is
 * the phase position; for a nested cycle it is the instance index (month-
 * name lists key off the instance, not the day-of-month). `instance` is 0
 * for an atomic cycle, so the selector is uniform.
 */
function resolveListLabel(
  calendar: Calendar,
  bag: CycleBag,
  cycle: CycleDefinition,
  labels: Extract<LabelSpec, { kind: 'list' }>,
  day: number,
): string | null {
  const pos = resolvePosition(calendar, bag, cycle, day);
  if (pos === null) return null;
  const labelIndex = cycle.structure.kind === 'atomic' ? pos.position : pos.instance;
  const idx = mod(labelIndex, labels.labels.length);
  const label = labels.labels[idx];
  return label ?? null;
}

/**
 * `product` LabelSpec: join each factor cycle's resolved label at `day`. A
 * factor without a label of its own contributes its numeric position; an
 * unknown factor is skipped. Returns `null` only when every factor drops
 * out (a fully malformed product). Factors join with a single space.
 */
function resolveProductLabel(
  calendar: Calendar,
  bag: CycleBag,
  spec: Extract<LabelSpec, { kind: 'product' }>,
  day: number,
  seen: ReadonlySet<string>,
): string | null {
  const parts: string[] = [];
  for (const factor of spec.factors) {
    const part = factorText(calendar, bag, factor.cycle, day, seen);
    if (part !== null) parts.push(part);
  }
  if (parts.length === 0) return null;
  return parts.join(' ');
}

/**
 * The text a named cycle contributes to a product/formula at `day`: its
 * label if it declares one (recursively, so a product of products works),
 * else its numeric position. `null` when the cycle is unknown or has been
 * entered already (a cyclic reference).
 */
function factorText(
  calendar: Calendar,
  bag: CycleBag,
  cycleName: string,
  day: number,
  seen: ReadonlySet<string>,
): string | null {
  if (seen.has(cycleName)) return null; // cyclic factor / placeholder
  const cycle = findCycle(bag, cycleName);
  if (cycle === undefined) return null;
  if (cycle.labels !== undefined) {
    const label = resolveLabel(calendar, bag, cycle, cycle.labels, day, seen);
    if (label !== null) return label;
  }
  // No label (or it didn't resolve): contribute the numeric position.
  const pos = resolvePosition(calendar, bag, cycle, day);
  if (pos === null) return null;
  const n = cycle.structure.kind === 'atomic' ? pos.position : pos.instance;
  return String(n);
}

/**
 * The numeric position a named cycle contributes to a `{cycle#}` formula
 * placeholder at `day`: the atomic phase position or the nested instance
 * index. `null` when unknown / unpositionable.
 */
function factorPosition(
  calendar: Calendar,
  bag: CycleBag,
  cycleName: string,
  day: number,
): number | null {
  const cycle = findCycle(bag, cycleName);
  if (cycle === undefined) return null;
  const pos = resolvePosition(calendar, bag, cycle, day);
  if (pos === null) return null;
  return cycle.structure.kind === 'atomic' ? pos.position : pos.instance;
}

/**
 * Matches a single formula placeholder: `{name}` (label) or `{name#}` /
 * `{name#k}` (numeric position, optionally offset by `k` for 1-indexed
 * display). The name is any run of characters other than `{`, `}`, `#`.
 */
const PLACEHOLDER = /\{([^{}#]+)(#(-?\d+)?)?\}/g;

/**
 * `formula` LabelSpec: expand the template's `{cycle}` / `{cycle#}` /
 * `{cycle#k}` placeholders against the named cycles, leaving all other text
 * (including unmatched braces) verbatim. An unknown placeholder cycle
 * expands to the empty string so a malformed template degrades gracefully
 * rather than leaking the raw `{…}`.
 */
function resolveFormulaLabel(
  calendar: Calendar,
  bag: CycleBag,
  spec: Extract<LabelSpec, { kind: 'formula' }>,
  day: number,
  seen: ReadonlySet<string>,
): string {
  return spec.format.replace(
    PLACEHOLDER,
    (_match, name: string, hash: string | undefined, offset: string | undefined): string => {
      if (hash !== undefined) {
        // Numeric placeholder: `{cycle#}` (offset 0) or `{cycle#k}`.
        const base = factorPosition(calendar, bag, name, day);
        if (base === null) return '';
        const k = offset !== undefined ? Number.parseInt(offset, 10) : 0;
        return String(base + k);
      }
      // Label placeholder: `{cycle}` → label (or position when label-less).
      const text = factorText(calendar, bag, name, day, seen);
      return text ?? '';
    },
  );
}
